import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { saveAs } from 'file-saver';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import {
  DEFAULT_PORTAL_CONFIG,
  ERROR_CODE,
  isGlobalDomain,
  MAGIC_LINK_REDEMPTION_RESULTS,
  MAGIC_LINK_STATES,
  PASSWORD_RESET_STATES,
} from './constants';
import { isSafeRedirectPath } from './url';

// Feature flag to toggle between old and new deal asset upload endpoints
// Set to true to use new endpoint (deal-assets-with-upload) that accepts file upload
// Set to false to use old endpoint (deal-assets) that accepts imageUrl
export const USE_NEW_DEAL_ASSET_UPLOAD_ENDPOINT = true;

import { APIClient as BaseAPIClient } from './apiClient';

const SIGNIN_PATH = '/signin';
const SIGNUP_PATH = '/register';
const RESET_PW_PATH = '/reset-password';
const MAGIC_LINK_PATH = '/magic-link';

export function redirectToSigninIfUnauthenticated(error) {
  if (_get(error, 'response.status') !== 401) return false;
  if (
    [SIGNIN_PATH, SIGNUP_PATH].indexOf(window.location.pathname) === -1 &&
    window.location.pathname.indexOf(RESET_PW_PATH) === -1 &&
    window.location.pathname.indexOf(MAGIC_LINK_PATH) === -1
  ) {
    const current = window.location.pathname + window.location.search;
    window.location.href = isSafeRedirectPath(current)
      ? `${SIGNIN_PATH}?redirect=${encodeURIComponent(current)}`
      : SIGNIN_PATH;
  }
  return true;
}

function handleRequestError(error, errorCallback) {
  if (redirectToSigninIfUnauthenticated(error)) return ERROR_CODE.AUTH;
  Sentry.captureException(error);
  errorCallback(error);
  return ERROR_CODE.CONNECTION;
}

class APIClient extends BaseAPIClient {
  constructor() {
    super();
    this.initClients();
  }

  initClients() {
    // CubeJS client initialization removed
  }

  async postRequest(path, options = {}, errorCallback = () => {}, dataPath = 'data.data') {
    try {
      const response = await this.client.post(path, options);
      const data = _get(response, dataPath);
      return data;
    } catch (error) {
      return handleRequestError(error, errorCallback);
    }
  }

  async postRequestConfig(path, options = {}, axiosConfig = {}, errorCallback = () => {}, dataPath = 'data.data') {
    try {
      const response = await this.client.post(path, options, axiosConfig);
      const data = _get(response, dataPath);
      return data;
    } catch (error) {
      return handleRequestError(error, errorCallback);
    }
  }

  async getRequest(path, options = {}, errorCallback = () => {}, dataPath = 'data.data') {
    try {
      const response = await this.client.get(path, options);
      const data = _get(response, dataPath);
      return data;
    } catch (error) {
      return handleRequestError(error, errorCallback);
    }
  }

  /**
   * Query auth server
   * @param {Object} formData Login form data
   */
  async login(formData) {
    try {
      const pageSessionStorage = window.sessionStorage;
      const termsRedirected = pageSessionStorage.getItem('termsRedirected');

      if (!termsRedirected) {
        pageSessionStorage.setItem('termsRedirected', true);
      }

      // Prepare request body based on domain
      const requestBody = isGlobalDomain()
        ? {
            ...formData,
            countryCode: 'global',
          }
        : {
            ...formData,
            countryCode: process.env.REACT_APP_REGION,
            country: 'SG',
          };

      // Prepare headers based on domain
      const requestHeaders = isGlobalDomain()
        ? {
            ...this.defaultHeaders,
            'x-shopback-country': 'global',
            'x-forwarded-host': 'https://www.shopback.sg',
          }
        : {
            ...this.defaultHeaders,
            'x-shopback-country': 'SG',
            'x-forwarded-host': 'https://www.shopback.sg',
          };

      return await this.client.post('/auth/login', requestBody, {
        headers: requestHeaders,
      });
    } catch (error) {
      if (error.response) {
        return error.response;
      }

      Sentry.captureException(error);
      return error;
    }
  }

  /**
   * Make call to API to logout
   */
  async logout() {
    try {
      return await this.client.post('/auth/logout');
    } catch (error) {
      return error;
    }
  }

  async requestMagicLink(email, redirectUrl = null) {
    const { SUCCESS, FAILED } = MAGIC_LINK_STATES;

    try {
      const { status } = await this.client.post(
        '/auth/magic-links',
        {
          email,
          countryCode: isGlobalDomain() ? 'global' : process.env.REACT_APP_REGION,
          ...(redirectUrl ? { redirectUrl } : {}),
        },
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      );
      return status === 204 ? SUCCESS : FAILED;
    } catch (error) {
      Sentry.captureException(error);
      return FAILED;
    }
  }

  async requestPasswordReset(email) {
    const { SUCCESS, FAILED } = PASSWORD_RESET_STATES;

    try {
      const { status } = await this.client.post(
        '/auth/password-reset',
        {
          email,
          countryCode: isGlobalDomain() ? 'global' : process.env.REACT_APP_REGION,
        },
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      );
      return status === 204 ? SUCCESS : FAILED;
    } catch (error) {
      Sentry.captureException(error);
      return FAILED;
    }
  }

  async validateResetPasswordToken(token) {
    if (_isEmpty(token)) {
      return false;
    }

    try {
      const { data } = await this.client.post('/auth/password-reset/validate', { token });
      return _get(data, 'data.status', false);
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  }

  async updatePassword({ token, newPassword, confirmPassword }) {
    if (_isEmpty(token) || _isEmpty(newPassword) || _isEmpty(confirmPassword)) {
      return false;
    }

    try {
      const { data } = await this.client.put(
        '/auth/password-update',
        {
          token,
          newPassword,
          confirmPassword,
          countryCode: isGlobalDomain() ? 'global' : process.env.REACT_APP_REGION,
        },
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      );

      return data;
    } catch (error) {
      Sentry.captureException(error);
      return {};
    }
  }

  async redeemMagicLink(token) {
    const { SUCCESS, EXPIRED, FAILED } = MAGIC_LINK_REDEMPTION_RESULTS;

    if (!token) {
      return FAILED;
    }

    try {
      await this.client.post(
        '/auth/magic-links/redeem',
        {
          token,
          countryCode: isGlobalDomain() ? 'global' : process.env.REACT_APP_REGION,
        },
        {
          headers: {
            ...this.defaultHeaders,
          },
        }
      );
      // Same as login: initUserEnv logs out a user with unaccepted terms unless this flag is set.
      window.sessionStorage.setItem('termsRedirected', true);
      return SUCCESS;
    } catch (error) {
      if (_get(error, 'response.status') === 401) {
        return EXPIRED;
      }
      Sentry.captureException(error);
      return FAILED;
    }
  }

  /**
   * API call for registration
   */
  async register(values) {
    try {
      return await this.client.post('/users/request-registration', values);
    } catch (error) {
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  /**
   * Call to get list of outlets that user has permissions
   */
  async getUserData() {
    return this.getRequest('/auth/me');
  }

  /**
   * Call to update user terms_accepted
   */
  async acceptTerms() {
    return this.client.post('/merchant-user/accept-terms');
  }

  /**
   * @todo check why the linter reports an error.
   */
  async getCustomMessages(outletIds = [], page = 1) {
    if (_isEmpty(outletIds)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }
    const outletIdsStr = outletIds.join(',');
    /**
     * first page only need 5 records
     */
    return this.getRequest(
      '/crm/messages',
      { params: { outletIds: outletIdsStr, page, size: page === 1 ? 5 : 8 } },
      null,
      'data'
    );
  }

  async getOutletCodes(outletIds, brandIds, page = 1, size = 10) {
    if (!Array.isArray(outletIds) || _isEmpty(outletIds) || !Array.isArray(brandIds) || _isEmpty(brandIds))
      return ERROR_CODE.INVALID_PARAMETER;
    const outletIdsStr = outletIds.join(',');
    const brandIdsStr = brandIds.join(',');
    return this.getRequest(
      '/terminal-account/search',
      {
        params: { outletIds: outletIdsStr, brandIds: brandIdsStr, page, size },
      },
      undefined,
      'data'
    );
  }

  async getOutletCodesCsv(outletIds, brandIds) {
    if (!Array.isArray(outletIds) || _isEmpty(outletIds) || !Array.isArray(brandIds) || _isEmpty(brandIds))
      return ERROR_CODE.INVALID_PARAMETER;
    const outletIdsStr = outletIds.join(',');
    const brandIdsStr = brandIds.join(',');
    return this.getRequest(
      '/terminal-account/download',
      {
        params: { outletIds: outletIdsStr, brandIds: brandIdsStr },
      },
      () => {},
      'data'
    );
  }

  async getCashbackRates(outletIds, brandIds) {
    if (!Array.isArray(outletIds) || _isEmpty(outletIds) || !Array.isArray(brandIds) || _isEmpty(brandIds))
      return ERROR_CODE.INVALID_PARAMETER;
    const outletIdsStr = outletIds.join(',');
    const brandIdsStr = brandIds.join(',');
    return this.getRequest('/merchant-portal/cashback-rates', {
      params: { outletIds: outletIdsStr, brandIds: brandIdsStr },
    });
  }

  async getInventories(brandId, outletIds) {
    if (_isEmpty(brandId) || !Array.isArray(outletIds) || _isEmpty(outletIds)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }

    const outletIdsStr = outletIds.join(',');
    return this.getRequest('/merchant-portal/inventory', { params: { brandIds: brandId, outletIds: outletIdsStr } });
  }

  async getUserInventory(args) {
    const { inventoryIds, outletId, brandId, next, limit = 10, startDate, endDate } = args;

    if (_isEmpty(inventoryIds) || _isEmpty(brandId) || _isEmpty(startDate) || _isEmpty(endDate)) {
      Sentry.captureMessage(
        JSON.stringify({
          message: 'Invalid params for getUserInventory',
          params: args,
        })
      );
      return ERROR_CODE.INVALID_PARAMETER;
    }
    const inventoryIdsString = inventoryIds.join(',');

    return this.getRequest('/user-inventory', {
      params: {
        inventory_ids: inventoryIdsString,
        brand_id: brandId,
        ...(outletId ? { outlet_id: outletId } : {}),
        ...(next ? { next } : {}),
        limit,
        start_date: startDate,
        end_date: endDate,
      },
    });
  }

  async getUserInventoryCsv({ listingCodes, outletId, brandId, dateRange }) {
    if (_isEmpty(listingCodes)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }

    return this.postRequest(
      '/merchant-portal/user-inventory/download',
      {
        inventoryCodes: listingCodes,
        outletId,
        brandId,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
      },
      () => {},
      'data'
    );
  }

  async getLoyalties(brandId, outletIds) {
    if (_isEmpty(brandId) || !Array.isArray(outletIds) || _isEmpty(outletIds)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }
    const outletIdsStr = outletIds.join(',');
    return this.getRequest('/merchant-portal/loyalties', { params: { brandIds: brandId, outletIds: outletIdsStr } });
  }

  async getLoyaltyCampaignCsv({ campaignId, outletIds }) {
    if (_isEmpty(campaignId) || _isEmpty(outletIds)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }

    return this.getRequest(
      '/loyalty-campaign/download',
      {
        params: {
          campaign_id: campaignId,
          outlet_ids: outletIds,
        },
      },
      () => {},
      'data'
    );
  }

  async getPartnerCashbacks(brandId, outletIds) {
    if (_isEmpty(brandId) || !Array.isArray(outletIds) || _isEmpty(outletIds)) {
      return ERROR_CODE.INVALID_PARAMETER;
    }
    const outletIdsStr = outletIds.join(',');
    return this.getRequest('/merchant-portal/partner-cashbacks', {
      params: { brandIds: brandId, outletIds: outletIdsStr },
    });
  }

  // queryCubeJs method removed - CubeJS integration has been removed

  /**
   * ADMIN API CALLS
   */
  async createMerchantGroup(formData) {
    try {
      const { data } = await this.client.post('/groups', {
        name: formData.name,
        outletIds: formData.outlets,
        invoicingUnitIds: formData.invoicingUnits,
      });

      return data;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getMerchantGroups() {
    return this.getRequest('/groups');
  }

  async createMerchantAccount(formData) {
    const { name, groupId, outletIds, invoicingUnitIds } = formData;

    try {
      const { data } = await this.client.post('/accounts', {
        name,
        groupId,
        outletIds,
        invoicingUnitIds,
      });
      return data;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getMerchantAccounts(groupIds) {
    return this.getRequest('/accounts', { params: { groupIds } });
  }

  async createAcl(formData) {
    const { merchantAccountIds, permissions = {}, filters = {} } = formData;

    try {
      const { data } = await this.client.post('/acls', {
        merchantAccountIds,
        permissions,
        filters,
      });
      return data;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getAcls() {
    return this.getRequest('/acls');
  }

  async createMerchantUser(formData) {
    const { firstName, lastName, email, password } = formData;

    try {
      const { data } = await this.client.post('/users', {
        firstName,
        lastName,
        email,
        password,
      });
      return data;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getAdminUsers() {
    return this.getRequest('/users');
  }

  /**
   * CRM API CALLS
   * TODO: Refactor API to separate
   * api calls per module
   */
  async getPendingSendCustomPushes(outlets) {
    const outletIdsStr = outlets.join();

    try {
      const response = await this.getRequest('/crm/messages/pending-send', { params: { outletIds: outletIdsStr } });
      return response;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async createCustomPush(customPush) {
    try {
      const response = await this.client.post('/crm/messages', customPush);

      return response;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getChartsForPage(params) {
    try {
      const response = await this.client.get('/merchant-portal/charts/load', {
        params,
      });
      return response;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getTransactionSummaryData(params) {
    try {
      const response = await this.client.post('/merchant-portal/transactions/summary', params);
      return response;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getTransactions(params) {
    try {
      const response = await this.client.get('/public/transaction', {
        params,
      });

      return response;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getUmbTransactions(params) {
    try {
      const response = await this.client.post('/merchant-portal/transactions', params);

      return response;
    } catch (err) {
      if (_get(err, 'response.status') === 400) {
        return _get(err, 'response.data');
      }
      Sentry.captureException(err);
      return ERROR_CODE.CONNECTION;
    }
  }

  async downloadUmbTransactions(params) {
    try {
      const response = await this.client.post('/merchant-portal/transactions/download', params);
      const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' });
      return this.saveFile(blob, `transactions-${Date.now()}.csv`);
    } catch (err) {
      if (_get(err, 'response.status') === 400) {
        return _get(err, 'response.data');
      }
      Sentry.captureException(err);
      return ERROR_CODE.CONNECTION;
    }
  }

  async downloadTransactionReport(params) {
    try {
      const response = await this.client.post('/merchant-portal/transactions/download', {
        params,
        responseType: 'blob',
      });

      return this.saveFile(response.data, `transactions-${Date.now()}.csv`);
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  async getProductSubscriptions() {
    // @todo add error monitoring here https://shopadmin.atlassian.net/browse/MS-400
    return this.getRequest('/merchant-portal/products/brand', {}, () => {}, 'data.data');
  }

  async getPortalConfig() {
    try {
      const response = await this.client.get('/merchant-portal/get-portal-config');
      return response.data;
    } catch (err) {
      return DEFAULT_PORTAL_CONFIG;
    }
  }

  saveFile = (data, name) => {
    saveAs(data, name);
  };

  // Helper method to create mission control API client with different baseURL
  createMissionControlClient() {
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: this.defaultHeaders,
      withCredentials: true,
    });
  }

  // Opportunity API calls
  async getMerchantOpportunities(params = {}) {
    const {
      email,
      merchantIds,
      country,
      campaignIds,
      startTime,
      endTime,
      status,
      page = 1,
      limit = 10,
      order,
    } = params;

    try {
      const missionControlClient = this.createMissionControlClient();

      const queryParams = { email, page, limit };

      // Add optional parameters if they exist
      if (merchantIds && merchantIds.length > 0) {
        queryParams.merchantIds = Array.isArray(merchantIds) ? merchantIds.join(',') : merchantIds;
      }
      if (country) {
        queryParams.countries = country;
      }
      if (campaignIds && campaignIds.length > 0) {
        queryParams.campaignIds = Array.isArray(campaignIds) ? campaignIds.join(',') : campaignIds;
      }
      if (startTime) {
        queryParams.startTime = startTime;
      }
      if (endTime) {
        queryParams.endTime = endTime;
      }
      if (status) {
        queryParams.status = status;
      }
      if (order) {
        queryParams.order = order;
      }

      const response = await missionControlClient.get('/mission-control-service/moop/opportunities', {
        params: queryParams,
      });
      return response.data;
    } catch (error) {
      if (_get(error, 'response.status') === 400) {
        return _get(error, 'response.data');
      }
      Sentry.captureException(error);
      return ERROR_CODE.CONNECTION;
    }
  }

  getMerchantOpportunitiesMerchants = async (params = {}) => {
    const { email, ...otherParams } = params;

    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/merchant-selection', {
        params: {
          email,
          page: 1,
          limit: 100, // Get more merchants by default
          ...otherParams,
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (getMerchantOpportunitiesMerchants):', error);
      throw error;
    }
  };

  getMerchantOpportunitiesCampaigns = async (params = {}) => {
    const { email, ...otherParams } = params;

    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/campaign-selection', {
        params: {
          email,
          page: 1,
          limit: 100, // Get more campaigns by default
          ...otherParams,
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (getMerchantOpportunitiesCampaigns):', error);
      throw error;
    }
  };

  merchantUpdateOpportunities = async (opportunities, email) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.put('/mission-control-service/moop/update-opportunities', {
        email,
        opportunities: opportunities,
      });
      return response.data;
    } catch (error) {
      console.error('API Error (merchantUpdateOpportunities):', error);
      throw error;
    }
  };

  // check if the opportunity is oversell
  checkOpportunitiesOversellStatus = async (opportunityIds, market) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      // convert array to comma separated string
      const idsParam = Array.isArray(opportunityIds) ? opportunityIds.join(',') : String(opportunityIds);

      const response = await missionControlClient.get('/mission-control-service/moop/opportunities/oversell-status', {
        params: {
          opportunityIds: idsParam,
          countries: market,
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (checkOpportunitiesOversellStatus):', error);
      throw error;
    }
  };

  downloadMerchantOpportunitiesCSV = async (params = {}) => {
    const { email, merchantIds, country, campaignIds, startTime, endTime, status } = params;

    try {
      const missionControlClient = this.createMissionControlClient();

      const queryParams = { email };

      // Add optional parameters if they exist
      if (merchantIds && merchantIds.length > 0) {
        queryParams.merchantIds = Array.isArray(merchantIds) ? merchantIds.join(',') : merchantIds;
      }
      if (country) {
        queryParams.country = country;
      }
      if (campaignIds && campaignIds.length > 0) {
        queryParams.campaignIds = Array.isArray(campaignIds) ? campaignIds.join(',') : campaignIds;
      }
      if (startTime) {
        queryParams.startTime = startTime;
      }
      if (endTime) {
        queryParams.endTime = endTime;
      }
      if (status) {
        queryParams.status = status;
      }

      const response = await missionControlClient.get('/mission-control-service/moop/download-csv', {
        params: queryParams,
        responseType: 'blob',
      });

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `opportunities-${timestamp}.csv`;

      // Save the file and return success status
      this.saveFile(response.data, filename);
      return { success: true };
    } catch (error) {
      console.error('API Error (downloadMerchantOpportunitiesCSV):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  getMerchantOpportunitiesInitInfo = async () => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/init-info');
      return response.data;
    } catch (error) {
      console.error('API Error (getMerchantOpportunitiesInitInfo):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  // Old endpoint - accepts JSON with imageUrl
  createDealAsset = async (dealAssetData) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.post('/mission-control-service/moop/deal-assets', dealAssetData);
      return response.data;
    } catch (error) {
      console.error('API Error (createDealAsset):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  // New mode - same endpoint but accepts multipart/form-data with file upload
  // Backend differentiates by Content-Type: multipart/form-data vs application/json
  createDealAssetWithUpload = async (formData) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.post('/mission-control-service/moop/deal-assets', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (createDealAssetWithUpload):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  getDealAssetById = async (id) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get(`/mission-control-service/moop/deal-assets/${id}`);
      return response.data;
    } catch (error) {
      console.error('API Error (getDealAssetById):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  getUploadedFileHistory = async (merchantId, country) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/uploaded-files', {
        params: { merchantId, country },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (getUploadedFileHistory):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  getFileUploadUrl = async (contentType, folderName = 'moop-images', bucketType = 'external') => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/upload-url', {
        params: {
          contentType,
          folderName,
          bucketType,
        },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (getFileUploadUrl):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  getFileDownloadUrl = async (fileKey, bucketType = 'external') => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.get('/mission-control-service/moop/download-url', {
        params: { fileKey, bucketType },
      });
      return response.data;
    } catch (error) {
      console.error('API Error (getFileDownloadUrl):', error);
      return null;
    }
  };

  saveUploadedFileToHistory = async (fileData) => {
    try {
      const missionControlClient = this.createMissionControlClient();
      const response = await missionControlClient.post('/mission-control-service/moop/uploaded-files', fileData);
      return response.data;
    } catch (error) {
      console.error('API Error (saveUploadedFileToHistory):', error);
      if (_get(error, 'response.status') === 400) {
        return { success: false, error: _get(error, 'response.data') };
      }
      Sentry.captureException(error);
      return { success: false, error: ERROR_CODE.CONNECTION };
    }
  };

  evaluateFeatureToggle = (flagName, ctx) => {
    const missionControlClient = this.createMissionControlClient();
    return missionControlClient.post(
      `/mission-control-service/v2/mission-control/moop/feature-toggles/${flagName}/evaluate`,
      ctx
    );
  };
}

const client = new APIClient();

export default client;
